import type { FormikProps } from 'formik';
import { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { object, ref, string } from 'yup';
import { Child, Container, StyledButton, StyledTextField } from '../../components';
import type { IContactFields, IContactValues, OnChangeEvent } from '../../types';
import { isValidPhoneNumberFormat } from '../../utils';

const Form = styled('form')`display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: stretch;
	width: 100%;`;

export const contactSchema = object({
	subject: string().required('Subject is required'),
	message: string().required('Message is required'),
	phoneNumber: string().test('format', 'Must be valid US, Mexico, or Canada phone number. Ex. 123-456-7890', value =>
		isValidPhoneNumberFormat(value)
	),
	emailAddress: string().email('Enter a valid email').required('Email is required'),
	confirmEmailAddress: string()
		.required('Confirm your email')
		.oneOf([ref('emailAddress')], 'Email does not match'),
});

export const ContactForm = (props: FormikProps<IContactValues> & { onV2: (token: string) => void }) => {
	const { values, errors, touched, handleChange, isValid, setFieldTouched } = props;
	const fields: IContactFields = {
		subject: { name: 'subject', label: 'Subject' },
		message: { name: 'message', label: 'Your Message' },
		firstName: { name: 'firstName', label: 'First Name' },
		lastName: { name: 'lastName', label: 'Last Name' },
		phoneNumber: { name: 'phoneNumber', label: 'Your Phone Number' },
		emailAddress: { name: 'emailAddress', label: 'Your Email' },
		confirmEmailAddress: { name: 'confirmEmailAddress', label: 'Confirm Your Email' },
	};

	const navigate = useNavigate();
	const [isHumanV2, setIsHumanV2] = useState(false);

	const change = (name: any, e: OnChangeEvent) => {
		e.persist();
		handleChange(e);
		setFieldTouched(name, true, false);
	};

	return (
		<Form onSubmit={props.handleSubmit}>
            <Container container spacing={32}>
				<Child>
					<StyledTextField
						value={values.subject || ''}
						name={fields.subject.name}
						label={fields.subject.label}
						helperText={touched.subject ? errors.subject : ''}
						error={touched.subject && errors.subject !== undefined}
						onChange={(e: OnChangeEvent) => change(fields.subject.name, e)}
					/>
                    <StyledTextField
						multiline
						rows={6}
						value={values.message || ''}
						name={fields.message.name}
						label={fields.message.label}
						helperText={touched.message ? errors.message : ''}
						error={touched.message && errors.message !== undefined}
						onChange={(e: OnChangeEvent) => change(fields.message.name, e)}
					/>
					<StyledTextField
						value={values.firstName || ''}
						name={fields.firstName.name}
						label={fields.firstName.label}
						helperText={touched.firstName ? errors.firstName : ''}
						error={touched.firstName && errors.firstName !== undefined}
						onChange={(e: OnChangeEvent) => change(fields.firstName.name, e)}
					/>
					<StyledTextField
						value={values.lastName || ''}
						name={fields.lastName.name}
						label={fields.lastName.label}
						helperText={touched.lastName ? errors.lastName : ''}
						error={touched.lastName && errors.lastName !== undefined}
						onChange={(e: OnChangeEvent) => change(fields.lastName.name, e)}
					/>
					<StyledTextField
						value={values.phoneNumber || ''}
						name={fields.phoneNumber.name}
						label={fields.phoneNumber.label}
						helperText={touched.phoneNumber ? errors.phoneNumber : ''}
						error={touched.phoneNumber && errors.phoneNumber !== undefined}
						onChange={(e: OnChangeEvent) => change(fields.phoneNumber.name, e)}
					/>
					<StyledTextField
						value={values.emailAddress}
						name={fields.emailAddress.name}
						label={fields.emailAddress.label}
						helperText={touched.emailAddress ? errors.emailAddress : ''}
						error={touched.emailAddress && errors.emailAddress !== undefined}
						onChange={(e: OnChangeEvent) => change(fields.emailAddress.name, e)}
					/>
					<StyledTextField
						value={values.confirmEmailAddress}
						name={fields.confirmEmailAddress.name}
						label={fields.confirmEmailAddress.label}
						helperText={touched.confirmEmailAddress ? errors.confirmEmailAddress : ''}
						error={touched.confirmEmailAddress && errors.confirmEmailAddress !== undefined}
						onChange={(e: OnChangeEvent) => change(fields.confirmEmailAddress.name, e)}
					/>
					<Child container item style={{ marginBottom: '16px' }}>
						<ReCAPTCHA
							sitekey={`6LeS1Z4UAAAAAA8YbI_o663eg166GUwB5Vgf2PzN`}
							onChange={(token: string | null) => {
								if (props.onV2) {
									props.onV2(token || '');
								}
								setIsHumanV2(true);
							}}
						/>
					</Child>
				</Child>
			</Container>
			<Child container item justify="center">
				<StyledButton style={{ width: '128px' }} onClick={() => navigate('/')}>
					Cancel
				</StyledButton>
				<StyledButton
					disabled={!isValid || !isHumanV2}
					type="submit"
					style={{ width: '128px', marginLeft: '32px' }}
				>
					Send
				</StyledButton>
			</Child>
		</Form>
	);
};
